import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { MdClose, MdAdd, MdRefresh, MdDelete, MdExpandMore } from 'react-icons/md'
import { NetworkRateCardZone } from './types'
import { COUNTRIES, DEFAULT_COUNTRY } from './countries'
import { expandPostcodeInput } from './parsePostcodeRanges'
import { isNeedsReviewPrefix } from './postcodeStatus'
import {
  AddressOption,
  LocationEntry,
  ZoneLocation,
  applyGeocodeResult,
  extractCity,
  useResolvePostcodes,
} from './useGeocode'

interface Props {
  zone: NetworkRateCardZone
  allZones: NetworkRateCardZone[]
  onLocationsChanged: (zoneId: string, locations: ZoneLocation[]) => void
  onZoneMetadataChanged: (zoneId: string, updates: { zoneName?: string; countryCode?: string }) => void
  onClose: () => void
}

function makeLoadingEntry(prefix: string): LocationEntry {
  return { prefix, city: null, geoPoint: null, isLoading: true, error: undefined, addressOptions: undefined }
}

// Sync ALL non-in-flight entries up to the editor — including errored / unresolved ones with
// `geoPoint: null`. Previously this filtered to only resolved entries, which silently dropped
// errored rows from the editor's `zone.locations` the moment the panel mounted. That defeated
// the editor-level "X of Y unresolved" banner and Save guard: opening a panel for a zone with
// 1 errored postcode would remove the offender, the banner would disappear, and Save would
// enable — letting the user ship a ratecard missing rows they never knew about.
//
// Keeping errored entries in the editor's zones state means the unresolved counter stays
// honest, the Save guard fires correctly, and re-opening the panel resurfaces them via the
// `status` / `resolveError` flags on each location. The save mapper still rejects null
// geoPoints — see NetworkRatecardEditor.handleSave.
function entriesToCommittedLocations(entries: LocationEntry[]): ZoneLocation[] {
  return entries
    .filter((e) => !e.isLoading)
    .map(({ prefix, city, geoPoint, status, resolveError }) => ({
      prefix,
      city,
      geoPoint,
      status,
      resolveError,
    }))
}

export function LocationEditorPanel({
  zone,
  allZones,
  onLocationsChanged,
  onZoneMetadataChanged,
  onClose,
}: Props) {
  const [postcodeInput, setPostcodeInput] = useState('')
  const [warnings, setWarnings] = useState<string[]>([])
  const [zoneName, setZoneName] = useState(zone.zoneName)
  const [countryCode, setCountryCode] = useState(zone.countryCode || DEFAULT_COUNTRY.code)
  // Derive UI-facing flags from the upstream `status` field set during the editor's auto-resolve.
  // - 'not_found' / 'error' / 'pending' (with no geoPoint yet) → render in the red error section
  //   with a Retry affordance. We treat 'pending' as needs-attention rather than as in-flight
  //   inside the panel, because the panel doesn't own the parent's auto-resolve — clicking Retry
  //   re-resolves the row locally, which is faster than waiting for the parent to finish.
  // - 'resolved' (or unset, for manually entered rows with a geoPoint) → resolved section.
  // `isLoading` here means "the panel is awaiting an HTTP response for this row" — that's
  // user-initiated only (handleAddPostcodes / handleRetry), never seeded from the upstream prop.
  const [entries, setEntries] = useState<LocationEntry[]>(() =>
    zone.locations.map((l) => {
      const needsResolution = !l.geoPoint || l.status === 'not_found' || l.status === 'error'
      return {
        ...l,
        isLoading: false,
        error: needsResolution
          ? l.resolveError ||
            (l.status === 'not_found'
              ? 'No match in postcode cache'
              : l.status === 'error'
              ? 'Lookup failed'
              : 'Awaiting resolution — click Retry')
          : undefined,
        addressOptions: undefined,
      }
    })
  )
  // Prefix whose address candidates are being lazily fetched from Postcodes/Resolve for the
  // dropdown. Saved entries load with addressOptions:undefined to avoid N resolve calls at
  // panel-open; we fetch on first dropdown-open instead. Drives the in-dropdown loading row.
  // (Open/close state is owned by Headless UI's Popover — no separate openOptionPrefix needed.)
  const [optionsLoadingPrefix, setOptionsLoadingPrefix] = useState<string | null>(null)
  const { resolveBatch, resolveSingle } = useResolvePostcodes()

  const errorEntries = useMemo(() => entries.filter((e) => e.error), [entries])
  const resolvedEntries = useMemo(
    () => entries.filter((e) => !e.error && !e.isLoading && e.geoPoint),
    [entries]
  )
  const loadingEntries = useMemo(() => entries.filter((e) => e.isLoading), [entries])

  // Sync entries up to parent whenever they change. Skip the mount-time fire — at mount, the
  // panel's entries are a 1:1 copy of `zone.locations`, so a sync there would push identical
  // data back up while still triggering the editor's `markDirty()` (handleLocationsChanged
  // can't tell the difference between a no-op replace and a real change). Subsequent runs come
  // from real user actions (add / remove / retry / pick option / "Remove all"), each of which
  // genuinely needs to push state up and is the right moment to flag the editor dirty.
  const isFirstEntriesSync = useRef(true)
  useEffect(() => {
    if (isFirstEntriesSync.current) {
      isFirstEntriesSync.current = false
      return
    }
    onLocationsChanged(zone.id, entriesToCommittedLocations(entries))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only fires on entries
  }, [entries])

  // Sync zone name and country code changes back to parent (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (zoneName !== zone.zoneName) {
        onZoneMetadataChanged(zone.id, { zoneName })
      }
    }, 500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneName])

  useEffect(() => {
    if (countryCode !== zone.countryCode) {
      onZoneMetadataChanged(zone.id, { countryCode })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode])

  const handleAddPostcodes = useCallback(async () => {
    if (!postcodeInput.trim()) return

    const { postcodes, warnings: parseWarnings } = expandPostcodeInput(postcodeInput)
    setWarnings(parseWarnings)
    if (postcodes.length === 0) return

    const existingPrefixes = new Set(entries.map((e) => e.prefix))
    const otherZonePrefixes = new Set(
      allZones.filter((z) => z.id !== zone.id).flatMap((z) => z.locations.map((l) => l.prefix))
    )

    const newPostcodes: string[] = []
    const duplicates: string[] = []
    for (const pc of postcodes) {
      if (existingPrefixes.has(pc)) {
        duplicates.push(`${pc} (this zone)`)
      } else if (otherZonePrefixes.has(pc)) {
        duplicates.push(`${pc} (other zone)`)
      } else {
        newPostcodes.push(pc)
        existingPrefixes.add(pc)
      }
    }

    if (duplicates.length > 0) {
      setWarnings((prev) => [...prev, `Skipped duplicates: ${duplicates.join(', ')}`])
    }

    if (newPostcodes.length === 0) {
      setPostcodeInput('')
      return
    }

    setPostcodeInput('')

    // Append placeholder loading entries for visual feedback during the await
    setEntries((prev) => [...prev, ...newPostcodes.map((p) => makeLoadingEntry(p))])

    let results: Awaited<ReturnType<typeof resolveBatch>>
    try {
      results = await resolveBatch(newPostcodes, countryCode)
    } catch (err) {
      // resolveBatch throws on contract violations (e.g. unsupported country code). Surface a
      // single zone-level warning instead of N identical per-row errors, and flip every loading
      // row in this batch to an error state so the user can retry/remove them.
      const message = err instanceof Error ? err.message : 'Postcode resolve failed'
      setWarnings((prev) => [...prev, message])
      setEntries((prev) =>
        prev.map((e) =>
          newPostcodes.includes(e.prefix) && e.isLoading
            ? { ...e, isLoading: false, error: message, status: 'error' as const, resolveError: message }
            : e
        )
      )
      return
    }

    setEntries((prev) => {
      const next = [...prev]
      const consumedIdx = new Set<number>()
      for (let i = 0; i < newPostcodes.length; i++) {
        const postcode = newPostcodes[i]
        const result = results[i]
        const targetIdx = next.findIndex(
          (e, idx) => e.prefix === postcode && e.isLoading && !consumedIdx.has(idx)
        )
        if (targetIdx === -1) continue
        consumedIdx.add(targetIdx)
        next[targetIdx] = result
          ? applyGeocodeResult(next[targetIdx], result)
          : {
              ...next[targetIdx],
              isLoading: false,
              error: 'No response from postcode cache',
              addressOptions: undefined,
              status: 'error' as const,
              resolveError: 'No response from postcode cache',
            }
      }
      return next
    })
  }, [postcodeInput, entries, allZones, zone.id, countryCode, resolveBatch])

  // Lazily fetch the address candidates for a resolved entry the first time its dropdown opens —
  // from the SAME source as the create flow: the internal Postcodes/Resolve endpoint. Saved entries
  // load without addressOptions (to avoid N resolve calls at panel-open); we fetch on first open
  // here. Leaves the entry's current city/geoPoint untouched — this only populates the options.
  const ensureAddressOptions = useCallback(
    async (prefix: string) => {
      const entry = entries.find((e) => e.prefix === prefix)
      if (!entry || (entry.addressOptions && entry.addressOptions.length > 0)) return

      setOptionsLoadingPrefix(prefix)
      try {
        let options: AddressOption[] = []
        try {
          const result = await resolveSingle(prefix, countryCode)
          if (result.success) options = result.options
        } catch {
          // Resolve contract error (e.g. unsupported country) — leave options empty; the
          // dropdown renders its "No suggestions" state.
        }
        setEntries((prev) => prev.map((e) => (e.prefix === prefix ? { ...e, addressOptions: options } : e)))
      } finally {
        setOptionsLoadingPrefix(null)
      }
    },
    [entries, resolveSingle, countryCode]
  )

  const handleRetry = useCallback(
    async (prefix: string) => {
      setEntries((prev) =>
        prev.map((e) =>
          e.prefix === prefix && e.error
            ? { ...e, isLoading: true, error: undefined, status: 'pending' as const, resolveError: undefined }
            : e
        )
      )
      try {
        const result = await resolveSingle(prefix, countryCode)
        setEntries((prev) =>
          prev.map((e) => (e.prefix === prefix && e.isLoading ? applyGeocodeResult(e, result) : e))
        )
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Postcode resolve failed'
        setEntries((prev) =>
          prev.map((e) =>
            e.prefix === prefix && e.isLoading
              ? { ...e, isLoading: false, error: message, status: 'error' as const, resolveError: message }
              : e
          )
        )
      }
    },
    [countryCode, resolveSingle]
  )

  const handleRemoveEntry = useCallback((prefix: string) => {
    setEntries((prev) => prev.filter((e) => e.prefix !== prefix))
  }, [])

  const handleSelectOption = useCallback((prefix: string, option: AddressOption) => {
    setEntries((prev) =>
      prev.map((e) =>
        e.prefix === prefix
          ? {
              ...e,
              city: extractCity(option.title),
              geoPoint: option.geoPoint,
              status: 'resolved' as const,
              resolveError: undefined,
            }
          : e
      )
    )
    // Closing the Popover is handled by the panel's `close()` at the call site.
  }, [])

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  return (
    <>
      <div
        className='fixed inset-0 z-drawerBackdrop transition-opacity duration-300 lg:opacity-0 lg:pointer-events-none bg-black/20'
        onClick={onClose}
        role='presentation'
      />
      <div className='fixed z-drawer bg-white shadow-xl flex flex-col overflow-hidden transition-transform duration-300 ease-out right-0 top-0 h-full w-[22rem] border-l border-gray-200 translate-x-0'>
        <div className='flex items-center justify-between px-5 py-4 bg-white'>
          <h2 className='text-lg font-semibold text-gray-900'>Zone Postcodes</h2>
          <button
            type='button'
            onClick={onClose}
            className='p-1 rounded hover:bg-gray-100 text-gray-400 transition-colors'
            aria-label='Close'
          >
            <MdClose className='w-5 h-5' />
          </button>
        </div>

        <div className='flex-1 overflow-y-auto p-4 space-y-4'>
          {/* Imported source — only for Copilot-imported zones. Shows the compact source-format
              token list so the user can see what their PDF/spreadsheet said before expansion. */}
          {zone.originalPostcodesInput && (
            <div className='rounded-lg bg-blue-50 border border-blue-200 px-3 py-2'>
              <p className='text-[10px] font-medium uppercase tracking-wide text-blue-700'>
                Imported as
              </p>
              <p className='mt-0.5 text-xs text-blue-900 break-words'>
                {zone.originalPostcodesInput}
              </p>
              <p className='mt-1 text-[10px] text-blue-600'>
                Expanded into {zone.locations.length} postcode prefixes below.
              </p>
            </div>
          )}

          {/* Zone Settings */}
          <div className='space-y-3'>
            <div>
              <label className='block text-xs font-medium text-gray-500 mb-1'>Zone Name</label>
              <input
                value={zoneName}
                onChange={(e) => setZoneName(e.target.value)}
                className={inputClass}
                placeholder='e.g. Zone A - Midlands'
              />
            </div>
            <div>
              <label className='block text-xs font-medium text-gray-500 mb-1'>Country</label>
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className={inputClass}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Add Postcodes */}
          <div>
            <label className='block text-xs font-medium text-gray-500 mb-1'>Add Postcodes</label>
            <div className='flex gap-2'>
              <input
                value={postcodeInput}
                onChange={(e) => setPostcodeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void handleAddPostcodes()
                  }
                }}
                className={inputClass}
                placeholder='e.g. G1-62, 64-82, 90, S1, B'
              />
              <button
                type='button'
                onClick={() => void handleAddPostcodes()}
                disabled={loadingEntries.length > 0 || !postcodeInput.trim()}
                className='flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <MdAdd className='w-4 h-4' />
                Add
              </button>
            </div>
            {warnings.length > 0 && (
              <div className='mt-2 space-y-1'>
                {warnings.map((w, i) => (
                  <p key={i} className='text-xs text-amber-600'>
                    {w}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Unresolved Entries — covers backend not_found, transient errors, AND pending rows
              from a Copilot import that haven't been picked up by the parent's auto-resolve yet.
              Save is blocked at the editor level until this section is empty. */}
          {errorEntries.length > 0 && (
            <div>
              <div className='mb-2 flex items-center justify-between gap-2'>
                <p className='text-xs font-medium text-red-600'>
                  {errorEntries.length} unresolved postcode{errorEntries.length !== 1 ? 's' : ''}
                </p>
                <button
                  type='button'
                  onClick={() => {
                    const prefixesToRemove = new Set(errorEntries.map((e) => e.prefix))
                    setEntries((prev) => prev.filter((e) => !prefixesToRemove.has(e.prefix)))
                  }}
                  className='text-[11px] font-medium text-red-600 hover:text-red-700 underline'
                  title='Remove every unresolved postcode in this zone'
                >
                  Remove all
                </button>
              </div>
              <div className='space-y-1'>
                {errorEntries.map((entry) => (
                  <div
                    key={entry.prefix}
                    className='flex items-center justify-between px-3 py-2 bg-red-50 border border-red-200 rounded-lg'
                  >
                    <div>
                      <span className='text-sm font-medium text-red-700'>{entry.prefix}</span>
                      <span className='text-xs text-red-500 ml-2'>{entry.error}</span>
                    </div>
                    <div className='flex items-center gap-1'>
                      <button
                        type='button'
                        onClick={() => void handleRetry(entry.prefix)}
                        className='p-1 hover:bg-red-100 rounded transition-colors text-red-500'
                        title='Retry'
                      >
                        <MdRefresh className='w-4 h-4' />
                      </button>
                      <button
                        type='button'
                        onClick={() => handleRemoveEntry(entry.prefix)}
                        className='p-1 hover:bg-red-100 rounded transition-colors text-red-400'
                        title='Remove'
                      >
                        <MdDelete className='w-4 h-4' />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resolved Entries */}
          {resolvedEntries.length > 0 && (
            <div>
              <p className='text-xs font-medium text-gray-500 mb-2'>Resolved ({resolvedEntries.length})</p>
              <div className='space-y-1'>
                {resolvedEntries.map((entry) => (
                  // Headless UI Popover owns open/close + gives outside-click & Esc dismissal, focus
                  // management, and aria-expanded/aria-haspopup on the trigger for free (frontend
                  // convention: new interactive UI uses Headless UI). The panel renders in-flow below
                  // the row (pushes the list down), matching the prior inline dropdown.
                  <Popover key={entry.prefix} className='relative'>
                    {({ open }) => (
                      <>
                        <div className='flex items-center justify-between px-3 py-1.5 bg-gray-50 rounded-lg group'>
                          <div className='flex items-center gap-2 min-w-0'>
                            <span className='text-sm font-mono font-medium text-gray-900'>{entry.prefix}</span>
                            {isNeedsReviewPrefix(entry.prefix) && (
                              <span
                                className='inline-flex items-center rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-700'
                                title='Single-character postcode — please verify manually'
                              >
                                Check
                              </span>
                            )}
                            {/* The prefix is already shown to the left, so just show the place name.
                                Every resolved entry is a dropdown — including saved ones loaded from an
                                existing card — so candidates are fetched lazily on first open from
                                Postcodes/Resolve and the user can change which place/coordinate a postcode
                                resolves to. The chevron is always shown so the affordance is discoverable. */}
                            <PopoverButton
                              onClick={() => {
                                // `open` is the pre-toggle state, so !open means we're opening — fetch then.
                                if (!open) void ensureAddressOptions(entry.prefix)
                              }}
                              title='Change the resolved address / coordinates'
                              className='text-xs text-gray-500 hover:text-blue-600 cursor-pointer flex items-center gap-0.5 min-w-0 outline-none'
                            >
                              <span className='truncate'>{entry.city || 'Unknown'}</span>
                              <MdExpandMore className='w-3 h-3 text-gray-400 flex-shrink-0' />
                            </PopoverButton>
                          </div>
                          <button
                            type='button'
                            onClick={() => handleRemoveEntry(entry.prefix)}
                            className='p-1 hover:bg-gray-200 rounded transition-colors text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0'
                            title='Remove'
                          >
                            <MdClose className='w-3.5 h-3.5' />
                          </button>
                        </div>

                        <PopoverPanel className='mt-1 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden'>
                          {({ close }) => (
                            <>
                              <div className='px-3 py-1.5 border-b border-gray-100 bg-gray-50'>
                                <p className='text-xs font-medium text-gray-500'>Select address for {entry.prefix}</p>
                              </div>
                              <div className='max-h-48 overflow-y-auto'>
                                {optionsLoadingPrefix === entry.prefix ? (
                                  <div className='flex items-center gap-2 px-3 py-2 text-xs text-gray-500'>
                                    <MdRefresh className='w-4 h-4 animate-spin' />
                                    Searching addresses…
                                  </div>
                                ) : entry.addressOptions && entry.addressOptions.length > 0 ? (
                                  entry.addressOptions.map((option) => {
                                    const isSelected =
                                      entry.geoPoint &&
                                      option.geoPoint.lat === entry.geoPoint.lat &&
                                      option.geoPoint.lon === entry.geoPoint.lon
                                    return (
                                      <button
                                        key={option.id}
                                        type='button'
                                        onClick={() => {
                                          handleSelectOption(entry.prefix, option)
                                          close()
                                        }}
                                        className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 transition-colors ${
                                          isSelected ? 'bg-emerald-50' : 'hover:bg-gray-50'
                                        }`}
                                      >
                                        <div
                                          className={`text-sm font-medium leading-tight ${
                                            isSelected ? 'text-emerald-700' : 'text-gray-900'
                                          }`}
                                        >
                                          {option.title}
                                          {isSelected && <span className='ml-1.5 text-emerald-500 text-xs'>&#10003;</span>}
                                        </div>
                                        <div className='mt-1 text-[10px] text-gray-400 font-mono'>
                                          {option.geoPoint.lat.toFixed(5)}, {option.geoPoint.lon.toFixed(5)}
                                        </div>
                                      </button>
                                    )
                                  })
                                ) : (
                                  <div className='px-3 py-2 text-xs text-gray-500'>
                                    No address suggestions found for this postcode.
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </PopoverPanel>
                      </>
                    )}
                  </Popover>
                ))}
              </div>
            </div>
          )}

          {/* Loading Entries */}
          {loadingEntries.length > 0 && (
            <div>
              <p className='text-xs font-medium text-gray-400 mb-2'>Resolving ({loadingEntries.length})</p>
              <div className='space-y-1'>
                {loadingEntries.map((entry) => (
                  <div
                    key={entry.prefix}
                    className='flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg animate-pulse'
                  >
                    <span className='text-sm font-mono text-gray-400'>{entry.prefix}</span>
                    <span className='text-xs text-gray-400'>Resolving...</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {entries.length === 0 && (
            <div className='text-center py-6'>
              <p className='text-sm text-gray-400'>Enter postcode prefixes above to add locations to this zone</p>
              <p className='text-xs text-gray-400 mt-1'>Supports ranges: G1-62, 64-82, 90</p>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
